import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listShopDashboardRepairRequests, scheduleShopRepairRequest } from '../../utils/partnerShopApi';
import { getShopAvailability, getShopClosures, createShopClosure, deleteShopClosure } from '../../utils/shopAuthApi';
import { getPartnerRequestTitle } from '../../utils/partnerRequestDisplay';
import useShopDashboardRequestUpdates from '../../hooks/useShopDashboardRequestUpdates';
import { isPartnerSite } from '../../utils/siteHost';
import AppointmentSlotPicker from '../AppointmentSlotPicker';
import AppointmentClockPicker from '../AppointmentClockPicker';

// ── Constants ────────────────────────────────────────────────────────────────
const VIEWS = ['day', 'week', 'month', 'year'];
const WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const HOUR_START = 6;   // 6 AM
const HOUR_END = 21;    // 9 PM (grid shows 6 AM – 9 PM)
const ROW_H = 56;       // px per hour
const MIN_EVENT_FRAC = 56 / ROW_H; // floor an event to ~1 grid-hour tall for layout + display
const DEFAULT_TZ = 'America/Denver';
const BROWSER_TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ; } catch (e) { return DEFAULT_TZ; } })();

// When a shop hasn't published hours (or they aren't reachable), the backend books against a
// 9–5 default — mirror that here so the green open-hours background always matches the slots.
const DEFAULT_BUSINESS_HOURS = Array.from({ length: 7 }, (_, d) => ({ weekday: d, opens_at: '09:00', closes_at: '17:00' }));

// Subtle green = the shop is open (bookable); light red = blocked off (no bookings).
const OPEN_BG = '#effaf1';
const CLOSURE_BG = 'rgba(239, 68, 68, 0.09)';
const CLOSURE_BORDER = '#f4c2c2';

// A distinct, stable color per job so adjacent appointments read apart (Google-Calendar style).
const EVENT_PALETTE = [
    { bg: '#fff1de', border: '#f0a132', text: '#a04a00' }, // amber
    { bg: '#d9fbf0', border: '#16b399', text: '#0a7f6a' }, // teal
    { bg: '#F2F2F2', border: '#8C8C8C', text: '#414141' }, // blue
    { bg: '#efe7ff', border: '#7c5cff', text: '#5b3fd6' }, // purple
    { bg: '#e9fbe9', border: '#3fae4f', text: '#217a2c' }, // green
    { bg: '#ffe6ee', border: '#f0559a', text: '#b02a63' }, // pink
];

// ── Date helpers ─────────────────────────────────────────────────────────────
// A ticket's scheduled_start_at is stored in UTC but belongs to the SHOP's timezone. We render
// everything in that timezone so appointments land on the right day and hour regardless of where
// the shop owner's browser is. zonedParts() extracts the wall-clock components in a given tz.
const zonedParts = (date, tz) => {
    try {
        const f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        });
        const p = Object.fromEntries(f.formatToParts(date).map((x) => [x.type, x.value]));
        return { y: +p.year, m: +p.month - 1, d: +p.day, h: +p.hour % 24, min: +p.minute };
    } catch (e) {
        return { y: date.getFullYear(), m: date.getMonth(), d: date.getDate(), h: date.getHours(), min: date.getMinutes() };
    }
};

// A "wall date" Date built at local noon — we only ever read its y/m/d, so noon keeps date math
// DST-safe. Used for the cursor, today, and grid iteration.
const wallDate = (parts) => new Date(parts.y, parts.m, parts.d, 12, 0, 0, 0);
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const addMonths = (date, n) => { const d = new Date(date); d.setDate(1); d.setMonth(d.getMonth() + n); d.setHours(12, 0, 0, 0); return d; };
const addYears = (date, n) => { const d = new Date(date); d.setFullYear(d.getFullYear() + n); return d; };
const startOfWeekMon = (date) => { const d = new Date(date); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); d.setHours(12, 0, 0, 0); return d; };
const sameYMD = (date, ev) => date.getFullYear() === ev.dY && date.getMonth() === ev.dM && date.getDate() === ev.dD;
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
const pad2 = (n) => String(n).padStart(2, '0');
const dateISO = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const formatDateUS = (iso) => {
    const [y, m, d] = String(iso || '').split('-');
    return y && m && d ? `${m}/${d}/${y}` : '';
};
const parseDateUS = (value) => {
    const match = String(value || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return '';
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
    ) return '';
    return `${year}-${pad2(month)}-${pad2(day)}`;
};
const parseHHMM = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return Number.isNaN(h) ? null : h + (m || 0) / 60;
};

const hashIndex = (str, mod) => {
    let h = 0;
    for (let i = 0; i < String(str).length; i += 1) h = (h * 31 + String(str).charCodeAt(i)) >>> 0;
    return h % mod;
};

const fracToTimeLabel = (frac) => {
    let h = Math.floor(frac);
    const m = Math.round((frac - h) * 60);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return m === 0 ? `${h} ${ampm}` : `${h}:${String(m).padStart(2, '0')} ${ampm}`;
};

const hourLabel = (h) => `${h % 12 || 12} ${h >= 12 ? 'PM' : 'AM'}`;
const clampFrac = (f) => Math.max(HOUR_START, Math.min(HOUR_END, f));

// The shop's open windows for one calendar date, as {startFrac,endFrac} bands (green bg).
// business_hours weekday follows PostgreSQL DOW (0 = Sunday), which is exactly Date.getDay().
const openWindowsForDate = (date, businessHours) => (businessHours || [])
    .filter((w) => w.weekday === date.getDay())
    .map((w) => ({ startFrac: parseHHMM(w.opens_at), endFrac: parseHHMM(w.closes_at) }))
    .filter((b) => b.startFrac != null && b.endFrac != null && b.endFrac > b.startFrac);

// Manual no-booking blocks for one date (red overlay). Full day covers the whole grid.
const closuresForDate = (date, closures) => {
    const iso = dateISO(date);
    return (closures || [])
        .filter((c) => c.on_date === iso)
        .map((c) => (c.is_full_day
            ? { fullDay: true, name: c.name, startFrac: HOUR_START, endFrac: HOUR_END }
            : { fullDay: false, name: c.name, startFrac: parseHHMM(c.start_time), endFrac: parseHHMM(c.end_time) }))
        .filter((c) => c.startFrac != null && c.endFrac != null);
};

// Lay overlapping appointments side by side: assign each a { lane, lanes } within its cluster
// of transitively-overlapping events, so the column width can be split evenly between them.
// Uses a floored duration (MIN_EVENT_FRAC) so two short back-to-back jobs that RENDER as
// overlapping blocks are also treated as overlapping here.
const assignLanes = (dayEvents) => {
    const items = dayEvents
        .map((ev) => ({ ev, start: ev.startFrac, end: Math.max(ev.endFrac, ev.startFrac + MIN_EVENT_FRAC) }))
        .sort((a, b) => a.start - b.start || a.end - b.end);
    const result = new Map();
    let cluster = [];
    let clusterEnd = -Infinity;
    const flush = () => {
        if (!cluster.length) return;
        const colEnds = []; // running end time per column
        cluster.forEach((it) => {
            let placed = false;
            for (let i = 0; i < colEnds.length; i += 1) {
                if (it.start >= colEnds[i]) { colEnds[i] = it.end; it.lane = i; placed = true; break; }
            }
            if (!placed) { it.lane = colEnds.length; colEnds.push(it.end); }
        });
        const lanes = colEnds.length;
        cluster.forEach((it) => result.set(it.ev.id, { lane: it.lane, lanes }));
        cluster = [];
        clusterEnd = -Infinity;
    };
    items.forEach((it) => {
        if (cluster.length && it.start >= clusterEnd) flush();
        cluster.push(it);
        clusterEnd = Math.max(clusterEnd, it.end);
    });
    flush();
    return result;
};

const getCustomerRealName = (request) => {
    const name = String(request?.customer?.display_name || '').trim();
    return name && name.toLowerCase() !== 'dash driver' ? name : '';
};
const getCustomerPhone = (request) => String(request?.customer?.phone_number || '').trim();
const getCustomerName = (request) => getCustomerRealName(request) || getCustomerPhone(request) || 'Customer';

const formatConflictAppointmentTime = (appointment) => {
    const start = appointment?.scheduled_start_at ? new Date(appointment.scheduled_start_at) : null;
    if (!start || Number.isNaN(start.getTime())) return 'Scheduled appointment';
    const tz = appointment?.scheduled_timezone || DEFAULT_TZ;
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(start);
    } catch (e) {
        return start.toLocaleString();
    }
};

const getConflictVehicleLabel = (appointment) => {
    const vehicle = appointment?.vehicle || {};
    const direct = String(vehicle.label || '').trim();
    if (direct) return direct;
    const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean);
    return parts.join(' ');
};

const getConflictIdentifier = (appointment) => {
    if (appointment?.ticket_number) return `Ticket ${appointment.ticket_number}`;
    const id = String(appointment?.request_id || '').trim();
    return id ? `Request ${id.slice(0, 8)}` : '';
};

// ── Small UI atoms ───────────────────────────────────────────────────────────
const NavButton = ({ onClick, label, children }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-[#d9d9d9] bg-white text-[#414141] transition hover:bg-[#f8f8f8]"
    >
        {children}
    </button>
);

const ChevronLeft = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18 9 12l6-6" /></svg>;
const ChevronRight = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>;

// Positioned event block for Day / Week time grids. lane/lanes split the column width
// so overlapping appointments sit side by side instead of stacking on top of each other.
const TimeGridEvent = ({ ev, onClick, compact = false, lane = 0, lanes = 1 }) => {
    const top = Math.max(0, ev.startFrac - HOUR_START) * ROW_H;
    const rawBottom = Math.min(HOUR_END - HOUR_START, Math.max(ev.startFrac + 0.25, ev.endFrac) - HOUR_START) * ROW_H;
    const height = Math.max(56, rawBottom - top);
    const c = EVENT_PALETTE[ev.colorIdx];
    const widthPct = 100 / lanes;
    const leftPct = lane * widthPct;
    const customerLine = ev.customerName ? `${ev.customerName} · ${ev.phone}` : (ev.phone || ev.customer);
    const size = compact || lanes > 1 ? 'text-[10px]' : 'text-[11px]';
    return (
        <button
            type="button"
            onClick={() => onClick(ev)}
            title={`${ev.timeText} · ${customerLine}${ev.title ? ` — ${ev.title}` : ''}`}
            style={{ top, height, left: `calc(${leftPct}% + 1px)`, width: `calc(${widthPct}% - 2px)`, background: c.bg, borderColor: c.border, color: c.text }}
            className="absolute z-10 flex flex-col overflow-hidden rounded-lg border border-l-[3px] px-2 py-0.5 text-left leading-tight transition hover:brightness-[0.98]"
        >
            <p className={`truncate font-black ${size}`}>{ev.timeText}</p>
            <p className={`truncate font-bold opacity-95 ${size}`}>{customerLine}</p>
            {ev.title && lanes < 3 && <p className={`truncate font-semibold opacity-80 ${size}`}>{ev.title}</p>}
        </button>
    );
};

const HourGutter = () => (
    <div className="w-14 shrink-0">
        {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i).map((h) => (
            <div key={h} style={{ height: ROW_H }} className="relative">
                <span className="absolute right-2 top-0 -translate-y-1/2 text-[11px] font-bold text-[#414141]">{hourLabel(h)}</span>
            </div>
        ))}
    </div>
);

const HourLines = () => (
    <>
        {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => i).map((i) => (
            <React.Fragment key={i}>
                <div style={{ top: i * ROW_H }} className="pointer-events-none absolute left-0 right-0 border-t border-[#ececec]" />
                <div style={{ top: i * ROW_H + ROW_H / 2 }} className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-[#f5f5f5]" />
            </React.Fragment>
        ))}
    </>
);

// One day's stacked layers: green open-hours background, red closure overlays, hour lines,
// then the side-by-side appointment blocks. Shared by Day (wide) and Week (compact) views.
const DayColumn = ({ date, dayEvents, businessHours, closures, compact, onEventClick }) => {
    const green = openWindowsForDate(date, businessHours);
    const red = closuresForDate(date, closures);
    const lanes = assignLanes(dayEvents);
    const fullDay = red.find((r) => r.fullDay);
    return (
        <div className="relative flex-1 border-l border-[#f2f2f2]" style={{ height: (HOUR_END - HOUR_START) * ROW_H }}>
            {green.map((b, i) => {
                const top = (clampFrac(b.startFrac) - HOUR_START) * ROW_H;
                const h = (clampFrac(b.endFrac) - clampFrac(b.startFrac)) * ROW_H;
                return <div key={`g${i}`} style={{ top, height: h, background: OPEN_BG }} className="pointer-events-none absolute inset-x-0 z-0" />;
            })}
            {red.map((b, i) => {
                const top = (clampFrac(b.startFrac) - HOUR_START) * ROW_H;
                const h = Math.max(18, (clampFrac(b.endFrac) - clampFrac(b.startFrac)) * ROW_H);
                return (
                    <div key={`r${i}`} style={{ top, height: h, background: CLOSURE_BG, borderColor: CLOSURE_BORDER }} className="pointer-events-none absolute inset-x-0 z-[1] border-y">
                        {!compact && (
                            <p className="px-2 pt-1 text-[10px] font-black uppercase tracking-[0.08em] text-[#b42318]">
                                No bookings{b.name ? ` · ${b.name}` : ''}
                            </p>
                        )}
                    </div>
                );
            })}
            <HourLines />
            {!compact && dayEvents.length === 0 && !fullDay && (
                <p className="absolute left-3 top-3 z-[2] text-sm font-semibold text-[#414141]">No jobs scheduled.</p>
            )}
            {dayEvents.map((ev) => {
                const l = lanes.get(ev.id) || { lane: 0, lanes: 1 };
                return <TimeGridEvent key={ev.id} ev={ev} onClick={onEventClick} compact={compact} lane={l.lane} lanes={l.lanes} />;
            })}
        </div>
    );
};

// ── Views ────────────────────────────────────────────────────────────────────
const DayView = ({ cursor, events, onEventClick, businessHours, closures }) => {
    const dayEvents = events.filter((e) => sameYMD(cursor, e)).sort((a, b) => a.startFrac - b.startFrac);
    return (
        <div className="rounded-2xl border border-[#d9d9d9] bg-white shadow-sm">
            <div className="flex py-3">
                <HourGutter />
                <DayColumn date={cursor} dayEvents={dayEvents} businessHours={businessHours} closures={closures} compact={false} onEventClick={onEventClick} />
            </div>
        </div>
    );
};

const WeekView = ({ cursor, events, onEventClick, today, businessHours, closures }) => {
    const weekStart = startOfWeekMon(cursor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    return (
        <div className="rounded-2xl border border-[#d9d9d9] bg-white shadow-sm">
            <div className="flex border-b border-[#F9F9F9]">
                <div className="w-14 shrink-0" />
                {days.map((d) => {
                    const isToday = isSameDay(d, today);
                    return (
                        <div key={dayKey(d)} className="flex-1 border-l border-[#f2f2f2] py-2 text-center">
                            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#414141]">{WEEKDAYS_SHORT[(d.getDay() + 6) % 7]}</p>
                            <p className={`text-sm font-black ${isToday ? 'text-[#D81F26]' : 'text-[#414141]'}`}>{d.getDate()}</p>
                        </div>
                    );
                })}
            </div>
            <div className="flex py-3">
                <HourGutter />
                {days.map((d) => {
                    const dayEvents = events.filter((e) => sameYMD(d, e)).sort((a, b) => a.startFrac - b.startFrac);
                    return (
                        <DayColumn key={dayKey(d)} date={d} dayEvents={dayEvents} businessHours={businessHours} closures={closures} compact onEventClick={onEventClick} />
                    );
                })}
            </div>
        </div>
    );
};

const MonthView = ({ cursor, events, onEventClick, today, closures }) => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 12);
    const gridStart = startOfWeekMon(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const weeks = [];
    for (let r = 0; r < 6; r += 1) {
        const week = cells.slice(r * 7, r * 7 + 7);
        if (week.some((d) => d.getMonth() === cursor.getMonth())) weeks.push(week);
    }
    const closedISO = new Set((closures || []).filter((c) => c.is_full_day).map((c) => c.on_date));
    return (
        <div className="overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm">
            <div className="grid grid-cols-7 border-b border-[#F9F9F9] bg-[#F9F9F9]">
                {WEEKDAYS_SHORT.map((w) => (
                    <div key={w} className="py-2 text-center text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">{w}</div>
                ))}
            </div>
            {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7">
                    {week.map((d) => {
                        const inMonth = d.getMonth() === cursor.getMonth();
                        const isToday = isSameDay(d, today);
                        const isClosed = closedISO.has(dateISO(d));
                        const dayEvents = events.filter((e) => sameYMD(d, e)).sort((a, b) => a.startFrac - b.startFrac);
                        return (
                            <div key={dayKey(d)} className={`min-h-[104px] border-b border-r border-[#f2f2f2] p-1.5 ${isClosed ? 'bg-[#fdf1f1]' : inMonth ? 'bg-white' : 'bg-[#fcfcfc]'}`}>
                                <div className="flex items-center justify-between px-1">
                                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[13px] font-black ${
                                        isToday ? 'bg-[#414141] text-white' : inMonth ? 'text-[#414141]' : 'text-[#c1c1c1]'
                                    }`}>{d.getDate()}</span>
                                    {isClosed
                                        ? <span className="text-[10px] font-black text-[#b42318]">Closed</span>
                                        : dayEvents.length > 0 && <span className="text-[10px] font-black text-[#414141]">{dayEvents.length} job{dayEvents.length === 1 ? '' : 's'}</span>}
                                </div>
                                <div className="mt-1 space-y-1">
                                    {dayEvents.slice(0, 3).map((ev) => {
                                        const c = EVENT_PALETTE[ev.colorIdx];
                                        return (
                                            <button
                                                key={ev.id}
                                                type="button"
                                                onClick={() => onEventClick(ev)}
                                                title={`${ev.customer} — ${ev.title}`}
                                                style={{ background: c.bg, color: c.text }}
                                                className="block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-bold transition hover:brightness-[0.97]"
                                            >
                                                {ev.title}
                                            </button>
                                        );
                                    })}
                                    {dayEvents.length > 3 && (
                                        <p className="px-1 text-[10px] font-bold text-[#414141]">+{dayEvents.length - 3} more</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const MiniMonth = ({ year, month, eventDayKeys, today, onPick }) => {
    const first = new Date(year, month, 1, 12);
    const gridStart = startOfWeekMon(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
    return (
        <button
            type="button"
            onClick={() => onPick(new Date(year, month, 1, 12))}
            className={`rounded-2xl border p-3 text-left transition hover:border-[#414141] ${isCurrentMonth ? 'border-[#d8d8d8] bg-[#f8f8f8]' : 'border-[#ececec] bg-white'}`}
        >
            <p className="mb-2 text-sm font-black text-[#414141]">{MONTHS_SHORT[month]}</p>
            <div className="grid grid-cols-7 gap-y-1">
                {WEEKDAY_INITIALS.map((w, i) => (
                    <span key={i} className="text-center text-[9px] font-bold text-[#414141]">{w}</span>
                ))}
                {cells.map((d) => {
                    const inMonth = d.getMonth() === month;
                    const hasEvents = inMonth && eventDayKeys.has(dayKey(d));
                    const isToday = isSameDay(d, today);
                    return (
                        <span key={dayKey(d)} className="relative flex flex-col items-center">
                            <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] ${
                                !inMonth ? 'text-transparent' : isToday ? 'bg-[#414141] font-black text-white' : 'font-semibold text-[#535353]'
                            }`}>{d.getDate()}</span>
                            {hasEvents && !isToday && <span className="absolute -bottom-0.5 h-1 w-1 rounded-full bg-[#16b399]" />}
                        </span>
                    );
                })}
            </div>
        </button>
    );
};

const YearView = ({ cursor, events, today, onPickMonth }) => {
    const year = cursor.getFullYear();
    const eventDayKeys = useMemo(() => {
        const s = new Set();
        events.forEach((e) => { if (e.dY === year) s.add(`${e.dY}-${e.dM}-${e.dD}`); });
        return s;
    }, [events, year]);
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MONTHS.map((_, m) => (
                <MiniMonth key={m} year={year} month={m} eventDayKeys={eventDayKeys} today={today} onPick={onPickMonth} />
            ))}
        </div>
    );
};

// ── Modals ───────────────────────────────────────────────────────────────────
const ModalShell = ({ title, subtitle, onClose, children }) => (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
        <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-black text-[#414141]">{title}</h2>
                    {subtitle && <p className="mt-1 text-sm font-semibold text-[#414141]">{subtitle}</p>}
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="text-[#414141] transition hover:text-[#414141]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
            </div>
            {children}
        </div>
    </div>
);

export const BlockTimeOffModal = ({ open, defaultDate, onClose, onCreate }) => {
    const [name, setName] = useState('Out of shop');
    const [date, setDate] = useState(defaultDate);
    const [dateText, setDateText] = useState(formatDateUS(defaultDate));
    const [fullDay, setFullDay] = useState(true);
    const [start, setStart] = useState('08:00');
    const [end, setEnd] = useState('08:30');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [conflict, setConflict] = useState(null);
    const datePickerRef = useRef(null);

    useEffect(() => {
        if (open) {
            setName('Out of shop');
            setDate(defaultDate);
            setDateText(formatDateUS(defaultDate));
            setFullDay(true);
            setStart('08:00');
            setEnd('08:30');
            setErr('');
            setConflict(null);
            setSaving(false);
        }
    }, [open, defaultDate]);

    useEffect(() => {
        setConflict(null);
    }, [date, fullDay, start, end]);

    if (!open) return null;

    const handleDateTextChange = (value) => {
        setDateText(value);
        const parsed = parseDateUS(value);
        if (parsed) setDate(parsed);
    };

    const handleNativeDateChange = (value) => {
        setDate(value);
        setDateText(formatDateUS(value));
    };

    const toggleFullDay = () => {
        setFullDay((current) => {
            const next = !current;
            if (!next) {
                setStart('08:00');
                setEnd('08:30');
            }
            return next;
        });
    };

    const submit = async () => {
        const parsedDate = parseDateUS(dateText) || date;
        if (!parsedDate) { setErr('Pick a date.'); return; }
        if (!parseDateUS(dateText)) { setErr('Use MM/DD/YYYY for the date.'); return; }
        if (!fullDay && start >= end) { setErr('Start time must be before end time.'); return; }
        setSaving(true); setErr('');
        try {
            await onCreate({
                name: name.trim() || 'Closed',
                on_date: parsedDate,
                is_full_day: fullDay,
                start_time: fullDay ? undefined : start,
                end_time: fullDay ? undefined : end,
                confirm_conflicts: Boolean(conflict),
            });
            onClose();
        } catch (e) {
            const detail = e?.data?.detail;
            if (e?.status === 409 && detail?.code === 'CLOSURE_CONFLICT') {
                setConflict(detail);
                setErr('');
                return;
            }
            setErr(e?.message || 'Could not block that time off.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell title="Block time off" subtitle="Prevent new bookings for a full day or a specific period while the shop is unavailable." onClose={onClose}>
            <label className="block">
                <span className="mb-1.5 block text-sm font-black text-[#414141]">Closure name</span>
                <input value={name} onChange={(e) => setName(e.target.value)}
                    className="h-11 w-full rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-bold text-[#414141] outline-none focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15" />
            </label>
            <label className="mt-4 block">
                <span className="mb-1.5 block text-sm font-black text-[#414141]">Date</span>
                <div className="relative">
                    <input
                        value={dateText}
                        onChange={(e) => handleDateTextChange(e.target.value)}
                        placeholder="MM/DD/YYYY"
                        inputMode="numeric"
                        className="h-11 w-full rounded-xl border border-[#d9d9d9] bg-white px-3 pr-11 text-sm font-bold text-[#414141] outline-none focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            try { datePickerRef.current?.showPicker?.(); } catch { datePickerRef.current?.focus?.(); }
                        }}
                        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-[#414141] transition hover:bg-[#F9F9F9]"
                        aria-label="Open date picker"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                    </button>
                    <input
                        ref={datePickerRef}
                        type="date"
                        lang="en-US"
                        value={date}
                        onChange={(e) => handleNativeDateChange(e.target.value)}
                        tabIndex={-1}
                        aria-hidden="true"
                        className="pointer-events-none absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 opacity-0"
                    />
                </div>
            </label>
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#ececec] bg-[#F9F9F9] px-3 py-2.5">
                <div>
                    <p className="text-sm font-black text-[#414141]">Block the full day</p>
                    <p className="text-xs font-semibold text-[#414141]">Turn off to enter a specific time range.</p>
                </div>
                <button type="button" role="switch" aria-checked={fullDay} onClick={toggleFullDay}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition ${fullDay ? 'bg-[#414141]' : 'bg-[#d4d4d4]'}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${fullDay ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
            </div>
            {!fullDay && (
                <div className="mt-3 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                        <AppointmentClockPicker id="block-start" value={start} onChange={setStart} openUp align="left" />
                    </div>
                    <span className="text-xs font-bold text-[#414141]">to</span>
                    <div className="min-w-0 flex-1">
                        <AppointmentClockPicker id="block-end" value={end} onChange={setEnd} openUp align="right" />
                    </div>
                </div>
            )}
            {conflict && (
                <div className="mt-4 rounded-xl border border-[#fbbf24] bg-[#fffbeb] px-3 py-3">
                    <p className="text-sm font-black text-[#92400e]">
                        {conflict.count || conflict.affected_appointments?.length || 0} appointment{(conflict.count || conflict.affected_appointments?.length) === 1 ? '' : 's'} already scheduled in this block.
                    </p>
                    <div className="mt-2 max-h-44 space-y-2 overflow-auto pr-1">
                        {(conflict.affected_appointments || []).map((appointment) => {
                            const customer = getCustomerName(appointment);
                            const title = getPartnerRequestTitle(appointment, 'Repair job');
                            const vehicle = getConflictVehicleLabel(appointment);
                            const identifier = getConflictIdentifier(appointment);
                            return (
                                <div key={appointment.request_id || `${appointment.scheduled_start_at}-${customer}`} className="rounded-lg border border-[#fde68a] bg-white px-2.5 py-2">
                                    <p className="text-xs font-black text-[#78350f]">{formatConflictAppointmentTime(appointment)}</p>
                                    <p className="mt-0.5 text-sm font-bold text-[#414141]">{customer}</p>
                                    <p className="text-xs font-semibold text-[#414141]">{[vehicle, title, identifier].filter(Boolean).join(' · ')}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {err && <p className="mt-3 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{err}</p>}
            <div className="mt-5 flex items-center justify-end gap-3">
                <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-black text-[#414141] transition hover:bg-[#f4f4f4]">Cancel</button>
                <button type="button" onClick={submit} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#D81F26] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-60">
                    {saving ? 'Blocking…' : conflict ? 'Block anyway' : 'Block bookings'}
                </button>
            </div>
        </ModalShell>
    );
};

const NewAppointmentModal = ({ open, shopId, timezone, defaultDate, minDate, jobs, onClose, onSchedule }) => {
    const [jobId, setJobId] = useState('');
    const [date, setDate] = useState(defaultDate);
    const [time, setTime] = useState('');
    const [reloadToken, setReloadToken] = useState(0);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (open) { setJobId(''); setDate(defaultDate); setTime(''); setReloadToken(0); setErr(''); setSaving(false); }
    }, [open, defaultDate]);

    if (!open) return null;

    const submit = async () => {
        if (!jobId) { setErr('Choose a job to schedule.'); return; }
        if (!date || !time) { setErr('Pick a date and an available time.'); return; }
        setSaving(true); setErr('');
        try {
            await onSchedule(jobId, { scheduled_date: date, scheduled_time: time, timezone, duration_minutes: 30, notes: '' });
            onClose();
        } catch (e) {
            if (Number(e?.status) === 409) {
                setTime('');
                setReloadToken((t) => t + 1);
                setErr((e?.message || '').replace(/^(SLOT_TAKEN|CLOSED):\s*/, '') || 'That time is unavailable. Please pick another.');
            } else {
                setErr(e?.message || 'Could not schedule this appointment.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell title="New appointment" subtitle="Schedule one of your jobs onto the calendar." onClose={onClose}>
            {jobs.length === 0 ? (
                <p className="rounded-xl bg-[#F9F9F9] px-3 py-4 text-sm font-semibold text-[#414141]">
                    No jobs are ready to schedule yet. A job becomes schedulable once its estimate is accepted.
                </p>
            ) : (
                <>
                    <label className="block">
                        <span className="mb-1.5 block text-sm font-black text-[#414141]">Job</span>
                        <select value={jobId} onChange={(e) => { setJobId(e.target.value); setErr(''); }}
                            className="h-11 w-full rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-bold text-[#414141] outline-none focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15">
                            <option value="">Select a job…</option>
                            {jobs.map((j) => (
                                <option key={j.request_id} value={j.request_id}>
                                    {getCustomerName(j)} — {getPartnerRequestTitle(j, 'Repair job')}
                                </option>
                            ))}
                        </select>
                    </label>
                    {jobId && (
                        <div className="mt-4">
                            <AppointmentSlotPicker
                                shopId={shopId}
                                minDate={minDate}
                                selectedDate={date}
                                onSelectDate={(d) => { setDate(d); setTime(''); setErr(''); }}
                                selectedTime={time}
                                onSelectSlot={(d, t) => { setDate(d); setTime(t); setErr(''); }}
                                reloadToken={reloadToken}
                                disabled={saving}
                            />
                        </div>
                    )}
                    {err && <p className="mt-3 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{err}</p>}
                    <div className="mt-5 flex items-center justify-end gap-3">
                        <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-black text-[#414141] transition hover:bg-[#f4f4f4]">Cancel</button>
                        <button type="button" onClick={submit} disabled={saving || !jobId || !time}
                            className="rounded-xl bg-[#D81F26] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60">
                            {saving ? 'Scheduling…' : 'Confirm appointment'}
                        </button>
                    </div>
                </>
            )}
        </ModalShell>
    );
};

// ── Right rail: Quick actions + Upcoming closures ─────────────────────────────
const QuickAction = ({ icon, title, subtitle, onClick }) => (
    <button type="button" onClick={onClick}
        className="flex w-full items-start gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition hover:border-[#d9d9d9] hover:bg-[#f9f9f9]">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F2F2F2] text-[#414141]">{icon}</span>
        <span className="min-w-0">
            <span className="block text-sm font-black text-[#414141]">{title}</span>
            <span className="block text-xs font-semibold text-[#414141]">{subtitle}</span>
        </span>
    </button>
);

const RailIcon = ({ children }) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;

const CalendarRail = ({ onNewAppointment, onEditHours, onBlockTime, onJumpToday, closures, onRemoveClosure, removingId }) => (
    <div className="space-y-4">
        <div className="rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm">
            <p className="text-base font-black text-[#414141]">Quick actions</p>
            <p className="mb-2 text-xs font-semibold text-[#414141]">Common calendar tasks</p>
            <QuickAction onClick={onNewAppointment} title="New appointment" subtitle="Schedule on selected day"
                icon={<RailIcon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18M12 14v4M10 16h4" /></RailIcon>} />
            <QuickAction onClick={onEditHours} title="Edit business hours" subtitle="Change weekly availability"
                icon={<RailIcon><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></RailIcon>} />
            <QuickAction onClick={onBlockTime} title="Block time off" subtitle="Block a full or partial day"
                icon={<RailIcon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18M8 15l8 0" /></RailIcon>} />
            <QuickAction onClick={onJumpToday} title="Jump to today" subtitle="Return to the current date"
                icon={<RailIcon><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 3v4h4" /></RailIcon>} />
        </div>

        <div className="rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm">
            <p className="mb-3 flex items-center gap-2 text-base font-black text-[#414141]">
                <RailIcon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></RailIcon>
                Upcoming closures
            </p>
            {closures.length === 0 ? (
                <p className="rounded-xl bg-[#F9F9F9] px-3 py-3 text-xs font-semibold text-[#414141]">No time off scheduled.</p>
            ) : (
                <div className="space-y-2">
                    {closures.map((c) => {
                        const d = new Date(`${c.on_date}T00:00:00`);
                        const dateLabel = Number.isNaN(d.getTime()) ? c.on_date
                            : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const timeLabel = c.is_full_day ? 'Full day' : `${fracToTimeLabel(parseHHMM(c.start_time))} – ${fracToTimeLabel(parseHHMM(c.end_time))}`;
                        return (
                            <div key={c.closure_id} className="flex items-center justify-between gap-2 rounded-xl bg-[#F9F9F9] px-3 py-2">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-black text-[#414141]">{c.name}</p>
                                    <p className="truncate text-xs font-semibold text-[#414141]">{dateLabel} · {timeLabel}</p>
                                </div>
                                <button type="button" onClick={() => onRemoveClosure(c.closure_id)} disabled={removingId === c.closure_id}
                                    aria-label="Remove closure" className="shrink-0 text-[#414141] transition hover:text-[#b42318] disabled:opacity-50">
                                    <RailIcon><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" /></RailIcon>
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    </div>
);

// ── Page ─────────────────────────────────────────────────────────────────────
const ShopCalendarPage = () => {
    const navigate = useNavigate();
    const [view, setView] = useState('day');
    const [cursor, setCursor] = useState(() => wallDate(zonedParts(new Date(), BROWSER_TZ)));
    const [requests, setRequests] = useState([]);
    const [shops, setShops] = useState([]);
    const [isDemoAdminView, setIsDemoAdminView] = useState(false);
    const [selectedShopId, setSelectedShopId] = useState('');
    const [businessHours, setBusinessHours] = useState([]);
    const [availTz, setAvailTz] = useState('');
    const [closures, setClosures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [blockOpen, setBlockOpen] = useState(false);
    const [apptOpen, setApptOpen] = useState(false);
    const [removingClosureId, setRemovingClosureId] = useState(null);
    const mountedRef = useRef(true);
    const syncedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const refresh = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const data = await listShopDashboardRepairRequests({
                partner_shop_id: selectedShopId || '',
            });
            if (!mountedRef.current) return;
            const nextShops = Array.isArray(data?.shops) ? data.shops : [];
            const partnerShops = nextShops.filter((shop) => shop?.shop_type !== 'nearby');
            const adminView = Boolean(data?.demoAdminEnabled);
            const fallbackShopId = partnerShops[0]?.partner_shop_id ? String(partnerShops[0].partner_shop_id) : '';
            const effectiveSelectedShopId = selectedShopId || fallbackShopId;
            const nextRequests = Array.isArray(data?.requests) ? data.requests : [];

            setShops(nextShops);
            setIsDemoAdminView(adminView);
            if (adminView && !selectedShopId && fallbackShopId) {
                setSelectedShopId(fallbackShopId);
            }
            setRequests(adminView && effectiveSelectedShopId
                ? nextRequests.filter((request) => String(request?.partner_shop_id || request?.partner_shop?.id || '') === effectiveSelectedShopId)
                : nextRequests);
            setError('');
        } catch (e) {
            if (mountedRef.current) setError(e?.message || 'Could not load the calendar.');
        } finally {
            if (mountedRef.current && !silent) setLoading(false);
        }
    }, [selectedShopId]);

    // Business hours + closures drive the green open-hours background, red closure overlays,
    // and the Upcoming closures rail. Session-scoped (shop-auth) — best-effort so the calendar
    // still renders appointments even where these aren't reachable.
    const refreshMeta = useCallback(async () => {
        if (isDemoAdminView && !selectedShopId) {
            setBusinessHours([]);
            setAvailTz('');
            setClosures([]);
            return;
        }
        const scope = selectedShopId ? { partnerShopId: selectedShopId } : {};
        try {
            const [avail, cls] = await Promise.all([
                getShopAvailability(scope).catch(() => null),
                getShopClosures(scope).catch(() => []),
            ]);
            if (!mountedRef.current) return;
            if (avail) {
                setBusinessHours(Array.isArray(avail.hours) ? avail.hours : []);
                setAvailTz(avail.timezone || '');
            }
            setClosures(Array.isArray(cls) ? cls : []);
        } catch { /* non-fatal */ }
    }, [isDemoAdminView, selectedShopId]);

    useEffect(() => { refresh(); refreshMeta(); }, [refresh, refreshMeta]);
    useShopDashboardRequestUpdates({ enabled: true, onUpdate: () => refresh({ silent: true }) });

    const calendarTz = useMemo(() => {
        if (availTz) return availTz;
        const withTz = (requests || []).find((r) => r?.scheduled_timezone || r?.partner_shop?.timezone);
        return withTz?.scheduled_timezone || withTz?.partner_shop?.timezone || BROWSER_TZ;
    }, [availTz, requests]);

    // The green open-hours background: the shop's own weekly hours, or the 9–5 default the
    // backend books against when none are configured.
    const effectiveHours = useMemo(
        () => (businessHours.length ? businessHours : DEFAULT_BUSINESS_HOURS),
        [businessHours],
    );

    const shopId = useMemo(
        () => selectedShopId
            || (requests || []).find((r) => r?.partner_shop_id || r?.partner_shop?.id)?.partner_shop_id
            || (requests || []).find((r) => r?.partner_shop?.id)?.partner_shop?.id || null,
        [requests, selectedShopId],
    );

    const today = useMemo(() => wallDate(zonedParts(new Date(), calendarTz)), [calendarTz]);
    const todayISO = useMemo(() => dateISO(today), [today]);

    useEffect(() => {
        if (!loading && !syncedRef.current) {
            syncedRef.current = true;
            setCursor(wallDate(zonedParts(new Date(), calendarTz)));
        }
    }, [loading, calendarTz]);

    const events = useMemo(() => {
        const seen = new Set();
        const uniqueRequests = (requests || []).filter((r) => {
            if (!r?.scheduled_start_at) return false;
            const phone = String(r?.customer?.phone_number || '').trim();
            const key = `${r.scheduled_start_at}|${phone || r.request_id || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        return uniqueRequests
            .map((r) => {
                const start = new Date(r.scheduled_start_at);
                if (Number.isNaN(start.getTime())) return null;
                const endDate = r.scheduled_end_at && !Number.isNaN(new Date(r.scheduled_end_at).getTime())
                    ? new Date(r.scheduled_end_at)
                    : new Date(start.getTime() + 60 * 60 * 1000);
                const sp = zonedParts(start, calendarTz);
                const ep = zonedParts(endDate, calendarTz);
                let startFrac = sp.h + sp.min / 60;
                let endFrac = ep.h + ep.min / 60;
                if (endFrac <= startFrac) endFrac = startFrac + 1;
                return {
                    id: r.request_id,
                    request: r,
                    dY: sp.y, dM: sp.m, dD: sp.d,
                    startFrac, endFrac,
                    timeText: fracToTimeLabel(startFrac),
                    title: getPartnerRequestTitle(r, 'Repair job'),
                    customer: getCustomerName(r),
                    customerName: getCustomerRealName(r),
                    phone: getCustomerPhone(r),
                    colorIdx: hashIndex(r.request_id || `${start.getTime()}`, EVENT_PALETTE.length),
                };
            })
            .filter(Boolean);
    }, [requests, calendarTz]);

    const todayCount = useMemo(() => events.filter((e) => sameYMD(today, e)).length, [events, today]);

    // Jobs a shop can place on the calendar: estimate accepted, not yet a terminal status.
    const schedulableJobs = useMemo(
        () => (requests || []).filter((r) => ['estimate_accepted', 'scheduled'].includes(String(r?.request_status || '').toLowerCase())),
        [requests],
    );

    const onEventClick = useCallback((ev) => {
        const dashboardPath = isPartnerSite() ? '/' : '/shop';
        navigate(`${dashboardPath}?request_id=${encodeURIComponent(ev.id)}`);
    }, [navigate]);

    const step = useCallback((dir) => {
        setCursor((c) => {
            if (view === 'day') return addDays(c, dir);
            if (view === 'week') return addDays(c, dir * 7);
            if (view === 'month') return addMonths(c, dir);
            return addYears(c, dir);
        });
    }, [view]);

    const resetToToday = useCallback(() => setCursor(wallDate(zonedParts(new Date(), calendarTz))), [calendarTz]);

    // Deep-link to the Availability card, not just the top of Settings.
    const handleEditHours = useCallback(() => navigate('/settings#availability'), [navigate]);

    const handleCreateClosure = useCallback(async (payload) => {
        await createShopClosure(payload, selectedShopId ? { partnerShopId: selectedShopId } : {});
        await refreshMeta();
        refresh({ silent: true });
    }, [refreshMeta, refresh, selectedShopId]);

    const handleRemoveClosure = useCallback(async (closureId) => {
        setRemovingClosureId(closureId);
        try {
            await deleteShopClosure(closureId, selectedShopId ? { partnerShopId: selectedShopId } : {});
            await refreshMeta();
            refresh({ silent: true });
        } catch (e) {
            setError(e?.message || 'Could not remove that closure.');
        } finally {
            setRemovingClosureId(null);
        }
    }, [refreshMeta, refresh, selectedShopId]);

    const handleScheduleJob = useCallback(async (requestId, payload) => {
        await scheduleShopRepairRequest(requestId, payload);
        await refresh({ silent: true });
        refreshMeta();
    }, [refresh, refreshMeta]);

    const rangeLabel = useMemo(() => {
        if (view === 'day') {
            return cursor.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        }
        if (view === 'week') {
            const ws = startOfWeekMon(cursor);
            const we = addDays(ws, 6);
            const sameMonth = ws.getMonth() === we.getMonth();
            const left = `${MONTHS_SHORT[ws.getMonth()]} ${ws.getDate()}`;
            const right = sameMonth ? `${we.getDate()}` : `${MONTHS_SHORT[we.getMonth()]} ${we.getDate()}`;
            return `${left}–${right}, ${we.getFullYear()}`;
        }
        if (view === 'month') return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
        return `${cursor.getFullYear()}`;
    }, [view, cursor]);

    const resetLabel = view === 'day' ? 'Today' : view === 'week' ? 'This week' : view === 'month' ? 'This month' : 'This year';
    const selectedDateISO = useMemo(() => dateISO(cursor), [cursor]);
    // Block/appointment default to the selected day, but never a past date for booking.
    const apptDefaultDate = selectedDateISO >= todayISO ? selectedDateISO : todayISO;
    const partnerShops = useMemo(() => shops.filter((shop) => shop?.shop_type !== 'nearby'), [shops]);

    return (
        <div className="flex h-full flex-col">
            <div className="mx-auto w-full max-w-7xl shrink-0 px-4 pt-6 md:px-8 md:pt-8">
                <div className="mb-4 flex items-start justify-between gap-4">
                    <h1 className="text-3xl font-black tracking-tight text-[#414141] md:text-[34px]">Calendar</h1>
                    <span className="rounded-full bg-[#F9F9F9] px-3 py-1.5 text-sm font-black text-[#414141]">{todayCount} today</span>
                </div>

                <div className="mb-4 inline-flex rounded-full bg-[#efe7d9] p-1">
                    {VIEWS.map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => setView(v)}
                            className={`min-w-[64px] rounded-full px-4 py-1.5 text-sm font-black capitalize transition ${
                                view === v ? 'bg-white text-[#414141] shadow-sm' : 'text-[#7a6a52] hover:text-[#4b4433]'
                            }`}
                        >
                            {v}
                        </button>
                    ))}
                </div>

                {isDemoAdminView && partnerShops.length > 0 && (
                    <div className="mb-4 flex flex-col gap-2 sm:max-w-sm">
                        <label htmlFor="calendar-shop-filter" className="text-xs font-black uppercase tracking-wide text-[#414141]">Shop</label>
                        <select
                            id="calendar-shop-filter"
                            value={selectedShopId}
                            onChange={(event) => setSelectedShopId(event.target.value)}
                            className="min-h-[42px] rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-bold text-[#414141] outline-none transition focus:border-[#D81F26]"
                        >
                            {partnerShops.map((shop) => (
                                <option key={shop.partner_shop_id} value={shop.partner_shop_id}>{shop.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="mb-4 flex items-center justify-between gap-3">
                    <NavButton onClick={() => step(-1)} label="Previous"><ChevronLeft /></NavButton>
                    <p className="flex-1 text-center text-lg font-black text-[#414141] md:text-xl">{rangeLabel}</p>
                    <div className="flex items-center gap-2">
                        <NavButton onClick={() => step(1)} label="Next"><ChevronRight /></NavButton>
                        <button
                            type="button"
                            onClick={resetToToday}
                            className="rounded-full border border-[#d9d9d9] bg-white px-4 py-2 text-sm font-black text-[#414141] transition hover:bg-[#f8f8f8]"
                        >
                            {resetLabel}
                        </button>
                    </div>
                </div>

                {error && (
                    <p className="mb-3 rounded-xl border border-red-100 bg-red-50 px-4 py-2.5 text-sm font-bold text-red-600">{error}</p>
                )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 pb-12 md:px-8 lg:flex-row lg:items-start">
                    <div className="min-w-0 flex-1">
                        {loading ? (
                            <div className="rounded-2xl border border-[#d9d9d9] bg-white p-10 text-center text-sm font-bold text-[#414141] shadow-sm">Loading calendar…</div>
                        ) : (
                            <>
                                {view === 'day' && <DayView cursor={cursor} events={events} onEventClick={onEventClick} businessHours={effectiveHours} closures={closures} />}
                                {view === 'week' && <WeekView cursor={cursor} events={events} onEventClick={onEventClick} today={today} businessHours={effectiveHours} closures={closures} />}
                                {view === 'month' && <MonthView cursor={cursor} events={events} onEventClick={onEventClick} today={today} closures={closures} />}
                                {view === 'year' && (
                                    <YearView cursor={cursor} events={events} today={today} onPickMonth={(d) => { setCursor(d); setView('month'); }} />
                                )}
                            </>
                        )}
                    </div>

                    <aside className="w-full shrink-0 lg:w-[300px]">
                        <CalendarRail
                            onNewAppointment={() => setApptOpen(true)}
                            onEditHours={handleEditHours}
                            onBlockTime={() => setBlockOpen(true)}
                            onJumpToday={resetToToday}
                            closures={closures}
                            onRemoveClosure={handleRemoveClosure}
                            removingId={removingClosureId}
                        />
                    </aside>
                </div>
            </div>

            <BlockTimeOffModal
                open={blockOpen}
                defaultDate={selectedDateISO}
                onClose={() => setBlockOpen(false)}
                onCreate={handleCreateClosure}
            />
            <NewAppointmentModal
                open={apptOpen}
                shopId={shopId}
                timezone={calendarTz}
                defaultDate={apptDefaultDate}
                minDate={todayISO}
                jobs={schedulableJobs}
                onClose={() => setApptOpen(false)}
                onSchedule={handleScheduleJob}
            />
        </div>
    );
};

export default ShopCalendarPage;
